import clsx from 'clsx'
import { useEffect, useState } from 'react'
import type { Category, MomentView } from '../types'
import { SignInChip } from './SignInChip'
import type { User } from '../lib/firebase'

export const HOT_SLUG = '__hot__'

export type ViewMode = 'feed' | 'curation'

interface Props {
  categories: Category[]
  activeSlug: string | null
  onSelect: (slug: string | null) => void
  user: User | null
  canEdit: boolean
  mode: ViewMode
  onModeChange: (m: ViewMode) => void
  muted: boolean
  onToggleMute: () => void
  onOpenMap: () => void
  /** 開「音樂時光」獨立播放器 */
  onOpenPlayer: () => void
  /** 是否在 feed 視圖（地圖/策展模式時 ActivePlayer 未掛載 → 隱藏播放控制，避免「按了沒反應」） */
  feedActive: boolean
  /** 當前正在播放的 moment — 給 header 中間的 player pill 顯示標題用 */
  activeMoment: MomentView | null
  paused: boolean
  onTogglePause: () => void
  /** 點 pill 中間標題 → 開分享 modal */
  onShare: () => void
  /** 點 pill 標題還能順便 scrollIntoView 回到目前播放的卡片 */
  onScrollToActive: () => void
}

export function CategoryNav({
  categories,
  activeSlug,
  onSelect,
  user,
  canEdit,
  mode,
  onModeChange,
  muted,
  onToggleMute,
  onOpenMap,
  onOpenPlayer,
  feedActive,
  activeMoment,
  paused,
  onTogglePause,
  onShare,
  onScrollToActive
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false)

  const select = (slug: string | null) => {
    onSelect(slug)
    setMenuOpen(false)
  }

  // ESC 關閉
  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  const activeLabel = (() => {
    if (activeSlug === null) return '全部'
    if (activeSlug === HOT_SLUG) return '🔥 吸睛度'
    return categories.find((c) => c.slug === activeSlug)?.name ?? '全部'
  })()
  const activeColor = activeSlug
    ? activeSlug === HOT_SLUG
      ? '#E85F39'
      : categories.find((c) => c.slug === activeSlug)?.color
    : undefined

  return (
    <div className="sticky top-0 z-30 border-b border-ink/10 bg-paper/90">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2 md:gap-6 md:px-8">
        <a href="/" className="shrink-0 leading-tight">
          <div className="font-serif text-sm font-bold tracking-tightest md:text-base">
            周深 · Moment
          </div>
          <div className="hidden text-[9px] tracking-[0.2em] text-ink-muted sm:block">
            A FAN CURATION
          </div>
        </a>

        {/* 桌機 inline */}
        {mode === 'feed' ? (
          <nav className="hidden min-w-0 flex-1 overflow-x-auto sm:block">
            <div className="flex items-center gap-1 whitespace-nowrap">
              <CategoryButton active={activeSlug === null} onClick={() => select(null)}>
                全部
              </CategoryButton>
              <CategoryButton active={activeSlug === HOT_SLUG} onClick={() => select(HOT_SLUG)}>
                🔥 吸睛度
              </CategoryButton>
              {categories.map((c) => (
                <CategoryButton
                  key={c.id}
                  active={activeSlug === c.slug}
                  onClick={() => select(c.slug)}
                >
                  {c.name}
                </CategoryButton>
              ))}
            </div>
          </nav>
        ) : (
          <div className="hidden min-w-0 flex-1 text-sm font-medium text-ink-soft sm:block">
            策展模式 · 拖拉卡片跨欄調整
          </div>
        )}

        {canEdit && (
          <div className="hidden shrink-0 items-center gap-0.5 rounded-full border border-ink/15 bg-paper-card/60 p-0.5 sm:flex">
            <ModeButton active={mode === 'feed'} onClick={() => onModeChange('feed')}>
              時間串
            </ModeButton>
            <ModeButton active={mode === 'curation'} onClick={() => onModeChange('curation')}>
              策展
            </ModeButton>
          </div>
        )}

        <div className="hidden shrink-0 items-center gap-2 sm:flex">
          <PlayerButton onClick={onOpenPlayer} />
          <MapButton onClick={onOpenMap} />
        </div>

        {/* Player pill：mute + play/pause + 當前曲目，三件包在一起。
            只在 feed 視圖顯示 — 地圖/策展模式 ActivePlayer 未掛載，顯示會「按了沒反應」 */}
        {feedActive && (
          <div className="hidden min-w-0 shrink sm:block">
            <PlayerPill
              moment={activeMoment}
              muted={muted}
              paused={paused}
              onToggleMute={onToggleMute}
              onTogglePause={onTogglePause}
              onClickTitle={() => {
                onScrollToActive()
                onShare()
              }}
            />
          </div>
        )}

        <div className="hidden shrink-0 sm:block">
          <SignInChip user={user} canEdit={canEdit} />
        </div>

        {/* 手機：當前 label + 地圖 + 播放 + 音量 + 漢堡 */}
        <div className="ml-auto flex items-center gap-2 sm:hidden">
          <span
            className="inline-flex items-center gap-1.5 text-xs text-ink-soft"
            style={activeColor ? { color: activeColor } : undefined}
          >
            {activeColor && (
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: activeColor }}
                aria-hidden
              />
            )}
            {activeLabel}
          </span>
          <PlayerButton onClick={onOpenPlayer} />
          <MapButton onClick={onOpenMap} />
          {feedActive && <PlayPauseButton paused={paused} onToggle={onTogglePause} />}
          {feedActive && <SoundButton muted={muted} onToggle={onToggleMute} />}
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/10 bg-paper-card text-ink transition active:scale-95"
            aria-label={menuOpen ? '關閉選單' : '開啟選單'}
            aria-expanded={menuOpen}
          >
            <Burger open={menuOpen} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <MobileMenu
          categories={categories}
          activeSlug={activeSlug}
          onSelect={select}
          user={user}
          canEdit={canEdit}
          mode={mode}
          onModeChange={(m) => {
            onModeChange(m)
            setMenuOpen(false)
          }}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </div>
  )
}

// ============================================================
// 手機 menu — 單欄 list，tap-to-close scrim，無 backdrop-blur 求效能
// ============================================================
function MobileMenu({
  categories,
  activeSlug,
  onSelect,
  user,
  canEdit,
  mode,
  onModeChange,
  onClose
}: Omit<
  Props,
  | 'onModeChange'
  | 'muted'
  | 'onToggleMute'
  | 'onOpenMap'
  | 'onOpenPlayer'
  | 'feedActive'
  | 'activeMoment'
  | 'paused'
  | 'onTogglePause'
  | 'onShare'
  | 'onScrollToActive'
> & {
  onModeChange: (m: ViewMode) => void
  onClose: () => void
}) {
  return (
    <>
      {/* scrim — 點空白處關閉 */}
      <div
        className="fixed inset-0 top-[52px] z-20 bg-ink/20 sm:hidden"
        onClick={onClose}
        aria-hidden
      />
      {/* menu 本體 — 從 header 底下掉下來 */}
      <div className="absolute inset-x-0 top-full z-30 overflow-hidden sm:hidden">
        <div
          className="max-h-[calc(100dvh-52px)] overflow-y-auto bg-paper shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <MenuSection label="分類">
            <ul>
              <MenuRow
                active={activeSlug === null}
                onClick={() => onSelect(null)}
                label="全部"
              />
              <MenuRow
                active={activeSlug === HOT_SLUG}
                onClick={() => onSelect(HOT_SLUG)}
                label="🔥 吸睛度"
                color="#E85F39"
              />
              {categories.map((c) => (
                <MenuRow
                  key={c.id}
                  active={activeSlug === c.slug}
                  onClick={() => onSelect(c.slug)}
                  label={c.name}
                  color={c.color}
                />
              ))}
            </ul>
          </MenuSection>

          {canEdit && (
            <MenuSection label="檢視模式">
              <div className="flex gap-2 px-4 py-1">
                <SegButton active={mode === 'feed'} onClick={() => onModeChange('feed')}>
                  時間串
                </SegButton>
                <SegButton active={mode === 'curation'} onClick={() => onModeChange('curation')}>
                  策展
                </SegButton>
              </div>
            </MenuSection>
          )}

          <MenuSection label="帳號">
            <div className="px-4 py-1">
              <SignInChip user={user} canEdit={canEdit} />
            </div>
          </MenuSection>
        </div>
      </div>
    </>
  )
}

function MenuSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-ink/5 py-2 last:border-b-0">
      <div className="px-5 pb-1 pt-2 text-[10px] uppercase tracking-[0.22em] text-ink-muted">
        {label}
      </div>
      {children}
    </div>
  )
}

function MenuRow({
  active,
  onClick,
  label,
  color
}: {
  active: boolean
  onClick: () => void
  label: string
  color?: string
}) {
  return (
    <li>
      <button
        onClick={onClick}
        className={clsx(
          'flex w-full items-center gap-3 px-5 py-3 text-left text-[15px] transition',
          active ? 'bg-ink/5 font-medium text-ink' : 'text-ink-soft active:bg-ink/5'
        )}
      >
        <span
          className="h-5 w-[3px] rounded-full transition"
          style={{ background: active && color ? color : active ? '#141414' : 'transparent' }}
          aria-hidden
        />
        {label}
        {active && <span className="ml-auto text-xs text-ink-muted">·</span>}
      </button>
    </li>
  )
}

function SegButton({
  active,
  onClick,
  children
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex-1 rounded-lg border px-4 py-2.5 text-sm font-medium transition',
        active
          ? 'border-ink bg-ink text-paper'
          : 'border-ink/15 bg-paper-card text-ink-soft active:bg-ink/5'
      )}
    >
      {children}
    </button>
  )
}

// 漢堡 / 關閉圖示（SVG，比 emoji 乾淨）
function Burger({ open }: { open: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d={open ? 'M3 3 L13 13 M13 3 L3 13' : 'M2 5 H14 M2 11 H14'}
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  )
}

function ModeButton({
  active,
  onClick,
  children
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'rounded-full px-3 py-1 text-xs transition',
        active ? 'bg-ink text-paper' : 'text-ink-soft hover:bg-ink/5'
      )}
    >
      {children}
    </button>
  )
}

function SoundButton({ muted, onToggle }: { muted: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/10 bg-paper-card text-ink transition hover:bg-ink/5 active:scale-95"
      aria-label={muted ? '打開聲音' : '靜音'}
      title={muted ? '打開聲音' : '靜音'}
    >
      <span className="text-base leading-none" aria-hidden>
        {muted ? '🔇' : '🔊'}
      </span>
    </button>
  )
}

/** ▶/⏸ 切換鈕 — 手機獨立顯示。桌機合在 PlayerPill 裡面。 */
function PlayPauseButton({ paused, onToggle }: { paused: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/10 bg-paper-card text-ink transition hover:bg-ink/5 active:scale-95"
      aria-label={paused ? '播放' : '暫停'}
      title={paused ? '播放' : '暫停'}
    >
      {paused ? (
        // ▶
        <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
          <path d="M3 2 L10 6 L3 10 Z" />
        </svg>
      ) : (
        // ⏸
        <svg width="10" height="11" viewBox="0 0 10 12" fill="currentColor" aria-hidden>
          <rect x="1" y="1.5" width="2.5" height="9" rx="0.5" />
          <rect x="6.5" y="1.5" width="2.5" height="9" rx="0.5" />
        </svg>
      )}
    </button>
  )
}

/**
 * 桌機 header 中間的「現在播放」pill：[♪] [▶/⏸] [標題]
 * - 整顆是一個 rounded-full 容器、高度 9（h-9）跟其他 icon button 對齊
 * - 標題截斷一行；點下去 → onClickTitle（呼叫端會 scrollIntoView + 開分享 modal）
 * - 沒有 activeMoment 時 fallback 顯示「— 還沒選曲 —」
 */
function PlayerPill({
  moment,
  muted,
  paused,
  onToggleMute,
  onTogglePause,
  onClickTitle
}: {
  moment: MomentView | null
  muted: boolean
  paused: boolean
  onToggleMute: () => void
  onTogglePause: () => void
  onClickTitle: () => void
}) {
  const dotColor = moment?.category?.color ?? '#666'
  return (
    <div className="flex h-9 min-w-0 items-center gap-1 rounded-full border border-ink/10 bg-paper-card pr-2 transition hover:border-ink/25">
      {/* 左：音量 */}
      <button
        onClick={onToggleMute}
        className="flex h-9 w-9 shrink-0 items-center justify-center text-ink transition hover:text-ink-soft active:scale-95"
        aria-label={muted ? '打開聲音' : '靜音'}
        title={muted ? '打開聲音' : '靜音'}
      >
        <span className="text-base leading-none" aria-hidden>
          {muted ? '🔇' : '🔊'}
        </span>
      </button>

      {/* 中左：play/pause */}
      <button
        onClick={onTogglePause}
        className="flex h-9 w-9 shrink-0 items-center justify-center text-ink transition hover:bg-ink/5 active:scale-95"
        aria-label={paused ? '播放' : '暫停'}
        title={paused ? '播放' : '暫停'}
      >
        {paused ? (
          <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
            <path d="M3 2 L10 6 L3 10 Z" />
          </svg>
        ) : (
          <svg width="10" height="11" viewBox="0 0 10 12" fill="currentColor" aria-hidden>
            <rect x="1" y="1.5" width="2.5" height="9" rx="0.5" />
            <rect x="6.5" y="1.5" width="2.5" height="9" rx="0.5" />
          </svg>
        )}
      </button>

      {/* 右：標題（可點）*/}
      <button
        onClick={moment ? onClickTitle : undefined}
        disabled={!moment}
        title={moment ? `${moment.title} — 點此分享 / 回到此 moment` : '還沒開始播'}
        className="flex min-w-0 max-w-[18rem] items-center gap-2 truncate rounded-r-full pl-1 pr-3 text-left text-xs text-ink-soft transition hover:text-ink disabled:cursor-default md:max-w-[22rem]"
      >
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ background: moment ? dotColor : 'transparent' }}
          aria-hidden
        />
        <span className="truncate">
          {moment ? moment.title : <span className="italic text-ink-muted">— 還沒選曲 —</span>}
        </span>
      </button>
    </div>
  )
}

/** 進入「音樂時光」獨立播放器 — 音符 icon */
function PlayerButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/10 bg-paper-card text-ink transition hover:bg-ink/5 active:scale-95"
      aria-label="音樂時光（隨機播放器）"
      title="音樂時光 · 隨機一首接一首"
    >
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden>
        <circle cx="6" cy="15" r="2.4" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="15" cy="13" r="2.4" stroke="currentColor" strokeWidth="1.4" />
        <path d="M8.4 15 L8.4 5 L17.4 3 L17.4 13" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
    </button>
  )
}

/** 進入全展開地圖模式的入口 — SVG 風格的小地圖 icon */
function MapButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/10 bg-paper-card text-ink transition hover:bg-ink/5 active:scale-95"
      aria-label="全展開地圖"
      title="全展開 · 快速瀏覽"
    >
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden>
        {/* 三摺地圖：兩道斜分隔線 + 外框 */}
        <path
          d="M1.5 4.5 L7 2.5 L13 4.5 L18.5 2.5 L18.5 15.5 L13 17.5 L7 15.5 L1.5 17.5 Z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
          fill="none"
        />
        <path d="M7 2.5 L7 15.5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M13 4.5 L13 17.5" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    </button>
  )
}

function CategoryButton({
  active,
  onClick,
  children
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'shrink-0 rounded-full px-3 py-1.5 text-sm transition',
        active ? 'bg-ink text-paper' : 'text-ink-soft hover:bg-ink/5'
      )}
    >
      {children}
    </button>
  )
}
